/**
 * Created by Ning Jiang on 2021-06-22.
 */

import {WinPresentationController} from "../../WinPresentationController";
import {BigWinView} from "./BigWinView";
import {TombstoneRip} from "../../../TombstoneRip";
import {gsap, TimelineLite} from "gsap";
import {TombstoneRipGameConfig} from "../../../TombstoneRipGameConfig";
import {SlotGame} from "@nolimitcity/slot-game/bin/SlotGame";
import {TombstoneRipSound} from "../../../sound/TombstoneRipSound";
import {GameMode} from "@nolimitcity/slot-game/bin/flowcontrol/GameMode";
import {TombstoneRipGameMode} from "../../../gameflow/TombstoneRipGameMode";
import {BelowStakeWinRestriction} from "../../../server/BelowStakeWinRestriction";

export class BigWinController extends WinPresentationController {

    private _view:BigWinView;
    private _levelTimeline:TimelineLite | null;
    private _currentSound:string | null;

    constructor() {
        super("BigWinController", true);
        this._view = new BigWinView();
    }

    protected hasWin():boolean {
        return TombstoneRip.model.parsedGameData.currentWinRatioConfig!.bigWinLevel > -1;
    }

    protected async createWinAnimation():Promise<any> {
        TombstoneRip.playFSWin = true;
        await new Promise<any>((resolve, reject) => {
            TombstoneRipSound.fadeMusic(0, 200);
            TombstoneRipSound.fadeAndStopSound({
                name : TombstoneRipSound.REELS_ON_FIRE,
                to : 0,
                duration : 200
            });
            if(TombstoneRip.background.currentMode === TombstoneRipGameMode.NORMAL_SUPER) {
                TombstoneRipSound.fadeAndStopSound({
                    name : TombstoneRipSound.RESPIN_LOOP,
                    to : 0,
                    duration : 300
                });
            }
            this.playBigWinLevel(0, resolve);
            this._view.start();
        });
    }

    private playBigWinLevel(level:number, resolve:(value:any) => void):void {
        const data = TombstoneRip.model.parsedGameData;
        if(level > data.currentWinRatioConfig!.bigWinLevel) {
            this._levelTimeline = null;
            this.playEnding(data.currentWinRatioConfig!.bigWinLevel, resolve);
        } else {
            const isEndLevel = level === data.currentWinRatioConfig!.bigWinLevel;
            const currentRatio = TombstoneRipGameConfig.WIN_RATIOS.find(r => r.bigWinLevel === level)!;
            const from:number = level === 0 ? 0 : data.playedBetValue * currentRatio.ratio;
            const to:number = isEndLevel ? data.singleWin : data.playedBetValue * TombstoneRipGameConfig.WIN_RATIOS.find(r => r.bigWinLevel === (level + 1))!.ratio;
            const tooSmallEndLevel:boolean = isEndLevel && this.isLevelWinTooSmall(level, from, to);
            const tl = new TimelineLite();
            tl.add(this.getLevelAnimation(level, currentRatio.duration, from, to, isEndLevel));
            tl.add(() => this.playBigWinLevel(level + 1, resolve));
            this._levelTimeline = tl;
            if(tooSmallEndLevel) {
                this._levelTimeline.progress(1);
            } else {
                this._flowStateController.setSkipCallback(() => this.skip!());
            }
        }
    }

    private getLevelAnimation(level:number, duration:number, from:number, to:number, isEndLevel:boolean):TimelineLite {
        const tl = new TimelineLite();
        tl.add([
            () => this.playLevelSound(level),
            () => TombstoneRip.bigWinCountUp.scale.set(1.2),
            TombstoneRip.bigWinCountUp.getCountUpAnimation(from, to, duration),
            gsap.to(TombstoneRip.bigWinCountUp.scale, {duration : duration, x : 1, y : 1, ease : `power4.out`}),
            this._view.getLevelAnimation(level, duration, isEndLevel)
        ]);
        return tl;
    }

    private isLevelWinTooSmall(level:number, from:number, to:number):boolean {
        if(level === 0) {
            return false;
        }

        return (to - from) <= (from * 0.1);
    }

    private playLevelSound(level:number):void {
        this.stopCurrentSound();
        this._currentSound = this.getLevelSoundName(level);
        TombstoneRipSound.soundPlayer.playTrack({name : this._currentSound, volume : 1});
    }

    private getLevelSoundName(level:number):string {
        if(!!SlotGame.apiPlugin.gameClientConfiguration.useCensoredSoundUS && level === 2) {
            return TombstoneRip.model.parsedGameData.mode === TombstoneRipGameMode.FREESPIN_SUPER ?
                "bigwinSFS3US" : "bigwin3US";
        }
        return TombstoneRip.model.parsedGameData.mode === TombstoneRipGameMode.FREESPIN_SUPER ?
            TombstoneRipSound.BIG_WIN_SUPER[level] : TombstoneRipSound.BIG_WIN[level];
    }

    private stopCurrentSound():void {
        if(this._currentSound) {
            TombstoneRipSound.soundPlayer.stopTrack(this._currentSound);
        }
        this._currentSound = null;
    }

    protected skip() {
        if(this._levelTimeline && this._levelTimeline.isActive()) {
            this._levelTimeline.progress(1);
        }
    }

    private playEnding(endLevel:number, resolve:(value:any) => void):void {
        this._flowStateController.setSkipCallback();
        this.stopCurrentSound();
        TombstoneRipSound.soundPlayer.playTrack({
            name : TombstoneRip.model.parsedGameData.mode === TombstoneRipGameMode.FREESPIN_SUPER ?
                TombstoneRipSound.BIG_WIN_END_SUPER : TombstoneRipSound.BIG_WIN_END
        });
        this.tryFadeBackMusic();
        // TombstoneRip.bigWinCountUp.scale.set(1);
        TombstoneRip.bigWinCountUp.stay();
        SlotGame.keypadPlugin.setWin(TombstoneRip.model.parsedGameData.totalWin, TombstoneRip.model.parsedGameData.showTotalWin, BelowStakeWinRestriction.isWinBelowStake());
        this._view.playEnding(endLevel);
        resolve(undefined);
    }

    private tryFadeBackMusic():void {
        const data = TombstoneRip.model.parsedGameData;

        // fs intro
        if(data.mode != GameMode.FREESPIN && data.nextMode === GameMode.FREESPIN) {
            return;
        }

        // super fs intro
        if(data.mode != TombstoneRipGameMode.FREESPIN_SUPER && data.nextMode === TombstoneRipGameMode.FREESPIN_SUPER) {
            return;
        }

        // fs outro + fs win cap
        if(data.mode != GameMode.NORMAL && data.nextMode === GameMode.NORMAL) {
            return;
        }

        // win cap
        if(data.brokeTheBank) {
            return;
        }

        TombstoneRipSound.fadeMusic(1, 200);
    }
}